import { Head } from "@inertiajs/react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { typedPatch } from "@/lib/typed-router"
import type { TryoutsApplicationsIndex } from "@/types/serializers"
import type RequestsUpdateTryoutApplication from "@/types/serializers/RequestsUpdateTryoutApplication"

const STATUS_OPTIONS = [
  { value: "registered", label: "Inscripto" },
  { value: "confirmed", label: "Confirmado" },
  { value: "attended", label: "Asistió" },
  { value: "no_show", label: "No se presentó" },
  { value: "selected", label: "Seleccionado" },
  { value: "not_selected", label: "No seleccionado" },
]

const STATUS_COLORS: Record<string, string> = {
  registered: "bg-blue-500/20 text-blue-400",
  confirmed: "bg-krown-accent/20 text-krown-accent",
  attended: "bg-green-500/20 text-green-400",
  no_show: "bg-red-500/20 text-red-400",
  selected: "bg-krown-crown/20 text-krown-crown",
  not_selected: "bg-krown-grey-50/20 text-krown-grey-50",
}

function statusLabel(value: string): string {
  return STATUS_OPTIONS.find((s) => s.value === value)?.label ?? value
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

export default function TryoutApplicationsIndex({
  tryout,
  applications,
  categories,
}: TryoutsApplicationsIndex) {
  const [filterCategory, setFilterCategory] = useState<string>("all")
  const [filterStatus, setFilterStatus] = useState<string>("all")

  const filtered = applications.filter((app) => {
    if (
      filterCategory !== "all" &&
      String(app.tryout_category.id) !== filterCategory
    )
      return false
    if (filterStatus !== "all" && app.status !== filterStatus) return false
    return true
  })

  function handleStatusChange(applicationId: number, newStatus: string) {
    typedPatch<RequestsUpdateTryoutApplication>(
      `/pruebas/${tryout.slug}/inscripciones/${applicationId}`,
      { tryout_application: { status: newStatus, notes: null } },
      { preserveState: true },
    )
  }

  function handleNotesChange(applicationId: number, notes: string) {
    typedPatch<RequestsUpdateTryoutApplication>(
      `/pruebas/${tryout.slug}/inscripciones/${applicationId}`,
      {
        tryout_application: {
          status: applications.find((a) => a.id === applicationId)!.status,
          notes: notes || null,
        },
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout>
      <Head title={`Inscripciones: ${tryout.title} | KrownFT`} />

      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <h1 className="mb-2 text-2xl font-bold text-white">Inscripciones</h1>
          <p className="text-muted-foreground mb-6">{tryout.title}</p>

          {/* Filters */}
          <div className="mb-4 flex flex-wrap gap-3">
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="text-muted-foreground flex items-center text-sm">
              {filtered.length} de {applications.length} inscripciones
            </div>
          </div>

          {/* Table */}
          {filtered.length > 0 ? (
            <div className="border-border overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Jugador</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Posición</TableHead>
                    <TableHead>Tutor</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Notas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell>
                        <button
                          className="text-krown-100 hover:underline"
                          onClick={() =>
                            app.player_profile.slug &&
                            window.open(
                              `/jugadores/${app.player_profile.slug}`,
                              "_blank",
                            )
                          }
                        >
                          {app.user.full_name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {app.tryout_category.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {app.player_profile.position}
                      </TableCell>
                      <TableCell className="text-sm">
                        {app.guardian_name ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(app.created_at)}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={app.status}
                          onValueChange={(v) => handleStatusChange(app.id, v)}
                        >
                          <SelectTrigger className="h-8 w-36">
                            <Badge
                              className={`${STATUS_COLORS[app.status] ?? ""} border-0`}
                            >
                              {statusLabel(app.status)}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Textarea
                          className="h-8 min-h-8 w-32 resize-none text-xs"
                          defaultValue={app.notes ?? ""}
                          placeholder="Notas..."
                          onBlur={(e) =>
                            handleNotesChange(app.id, e.target.value)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                No hay inscripciones que coincidan con los filtros.
              </p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
